/**
 * @fileoverview State and save flow for the category edit modal.
 * @module hooks/useCategoryEditModal
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  beginSubcategoryEdit,
  buildNewSubcategory,
  categoryDraftFrom,
  emptyCategoryDraft,
  persistedSubcategories,
  renameSubcategory,
  saveCategoryEdits,
  subcategoryNameTaken,
  toggleSubcategoryActive,
} from "../modals/CategoryEditModal/categoryEditState.js";

/**
 * The success message stays up for 3 seconds after the dialog closes.
 */
const SUCCESS_MESSAGE_DURATION_MS = 3000;

/**
 * Opens the native dialog and copies the category into a draft when `open` becomes true.
 * `category` is intentionally not a dependency: including it would reset the draft while the user is still editing.
 * Closing the dialog clears the name field, both error messages, and the "add subcategory" box.
 */
const useCategoryDialogLifecycle = ({
  open,
  onClose,
  category,
  dialogRef,
  setTempCategory,
  setCategoryMessageError,
  setSubcategoryMessageError,
  setSubcategoryName,
  setShowCreateSubcategory,
}) => {
  useEffect(() => {
    const handleClose = () => {
      setCategoryMessageError("");
      setSubcategoryMessageError("");
      setSubcategoryName("");
      setShowCreateSubcategory(false);
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      setTempCategory(categoryDraftFrom(category));
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
    // category is applied only when the dialog opens. Listing it here would
    // reset the draft while the user is still editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);
};

/**
 * Edit flow for one category.
 * Adding a subcategory rejects a duplicate name and puts the new row at the top of the list.
 * Saving strips the UI-only `editable` and `clientKey` fields before the PUT.
 *
 * Starting an edit marks that row `editable` and clears the flag on every other row,
 * so only one rename input is open. The draft name lives in `tempSubcategory` until save.
 *
 * The success flag is cleared on a timer after close. Unmounting the page before those 3 seconds
 * elapse does not cancel the timeout; the modal is expected to stay mounted on the catalog page.
 * A failed token refresh signs the user out and sends them home instead of showing the success message.
 */
const useCategoryEditModal = ({ open, onClose, category, onUpdateCategory }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const [subcategoryName, setSubcategoryName] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showCreateSubcategory, setShowCreateSubcategory] = useState(false);
  const [categoryMessageError, setCategoryMessageError] = useState("");
  const [subcategoryMessageError, setSubcategoryMessageError] = useState("");
  const [tempSubcategory, setTempSubcategory] = useState(null);
  const [tempCategory, setTempCategory] = useState(emptyCategoryDraft);

  /**
   * Updates the category name from the text input.
   * @param {Event} event - Input change event.
   * @returns {void}
   */
  const handleCategoryNameChange = useCallback((event) => {
    setTempCategory((prev) => ({
      ...prev,
      nombre: event.target.value,
    }));
  }, []);

  /**
   * Clears the category name and the "name already exists" message.
   * @returns {void}
   */
  const handleClearCategoryName = useCallback(() => {
    setCategoryMessageError("");
    setTempCategory((prev) => ({
      ...prev,
      nombre: "",
    }));
  }, []);

  /**
   * Toggles whether the category itself is active.
   * @returns {void}
   */
  const onCategorySwitchChange = useCallback(() => {
    setTempCategory((prev) => ({
      ...prev,
      activo: !prev.activo,
    }));
  }, []);

  /**
   * Stores the name typed in the add-subcategory box.
   * @param {Event} event - Input change event.
   * @returns {void}
   */
  const handleSubcategoryNameChange = useCallback((event) => {
    setSubcategoryName(event.target.value);
  }, []);

  /**
   * Shows the add-subcategory input.
   * @returns {void}
   */
  const handleShowCreateSubcategory = useCallback(() => {
    setShowCreateSubcategory(true);
  }, []);

  /**
   * Updates the name of the subcategory currently being renamed.
   * @param {string} nombre - Text from the rename input.
   * @returns {void}
   */
  const handleTempSubcategoryNameChange = useCallback((nombre) => {
    setTempSubcategory((prev) => ({
      ...prev,
      nombre,
    }));
  }, []);

  /**
   * Adds a subcategory at the top of the list when the name is not already used.
   * @returns {void}
   */
  const handleAddSubcategory = useCallback(() => {
    const newSubcategory = buildNewSubcategory(subcategoryName);
    if (subcategoryNameTaken(tempCategory.subcategorias, newSubcategory.nombre)) {
      setSubcategoryMessageError(
        t("components.categoryEditModal.subcategoryExistsError"),
      );
      return;
    }

    setTempCategory((prev) => ({
      ...prev,
      subcategorias: [newSubcategory].concat(prev.subcategorias),
    }));
    setSubcategoryName("");
    setSubcategoryMessageError("");
    setShowCreateSubcategory(false);
  }, [subcategoryName, tempCategory.subcategorias, t]);

  /**
   * Toggles `activo` on one subcategory.
   * @param {number} subcategoryIndex - Row to toggle.
   * @returns {void}
   */
  const onSubcategorySwitchChange = useCallback(
    (subcategoryIndex) => {
      setTempCategory((prev) => ({
        ...prev,
        subcategorias: toggleSubcategoryActive(
          prev.subcategorias,
          subcategoryIndex,
        ),
      }));
    },
    [],
  );

  /**
   * Opens one subcategory for rename and closes any other editor.
   * @param {number} subcategoryIndex - Row to edit.
   * @returns {void}
   */
  const handleEditClick = useCallback(
    (subcategoryIndex) => {
      const next = beginSubcategoryEdit(
        tempCategory.subcategorias,
        subcategoryIndex,
      );
      setTempSubcategory(next.tempSubcategory);
      setTempCategory({
        ...tempCategory,
        subcategorias: next.subcategorias,
      });
    },
    [tempCategory],
  );

  /**
   * Saves a renamed subcategory when the new name does not collide with another row.
   * @param {number} subcategoryIndex - Row that was being renamed.
   * @returns {void}
   */
  const handleEditSubcategory = useCallback(
    (subcategoryIndex) => {
      if (
        subcategoryNameTaken(
          tempCategory.subcategorias,
          tempSubcategory.nombre,
          subcategoryIndex,
        )
      ) {
        setSubcategoryMessageError(
          t("components.categoryEditModal.subcategoryExistsError"),
        );
        return;
      }
      setTempSubcategory(null);
      setTempCategory((prev) => ({
        ...prev,
        subcategorias: renameSubcategory(
          prev.subcategorias,
          subcategoryIndex,
          tempSubcategory.nombre,
        ),
      }));
    },
    [t, tempCategory.subcategorias, tempSubcategory],
  );

  /**
   * Persists the category. UI-only subcategory fields are removed before the request.
   * @returns {Promise<void>}
   */
  const handleUpdateCategory = useCallback(async () => {
    const categoryData = {
      _id: category._id,
      ...tempCategory,
      subcategorias: persistedSubcategories(tempCategory.subcategorias),
    };
    await saveCategoryEdits({
      categoryData,
      navigate,
      onSaved: (savedCategory) => {
        setCategoryMessageError("");
        setSubcategoryMessageError("");
        onUpdateCategory(savedCategory);
        onClose();
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, SUCCESS_MESSAGE_DURATION_MS);
      },
      onConflict: () => {
        setCategoryMessageError(
          t("components.categoryEditModal.categoryExistsError"),
        );
      },
    });
  }, [category._id, tempCategory, navigate, onClose, onUpdateCategory, t]);

  useCategoryDialogLifecycle({
    open,
    onClose,
    category,
    dialogRef,
    setTempCategory,
    setCategoryMessageError,
    setSubcategoryMessageError,
    setSubcategoryName,
    setShowCreateSubcategory,
  });

  return {
    t,
    open,
    onClose,
    dialogRef,
    tempCategory,
    tempSubcategory,
    subcategoryName,
    showCreateSubcategory,
    showSuccessMessage,
    categoryMessageError,
    subcategoryMessageError,
    handleCategoryNameChange,
    handleClearCategoryName,
    onCategorySwitchChange,
    handleSubcategoryNameChange,
    handleShowCreateSubcategory,
    handleAddSubcategory,
    handleTempSubcategoryNameChange,
    handleEditSubcategory,
    onSubcategorySwitchChange,
    handleEditClick,
    handleUpdateCategory,
  };
};

export default useCategoryEditModal;
